import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, PenLine, BookText, Loader2, Eye, Download, MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  StoryWriterDialog, loadWriterSettings, saveWriterSettings,
  loadWorldWriterDefaults, type StoryWriterSettings,
} from "./StoryWriterConfig";
import {
  EntryCard, EntryCollectionReader, StoryModeDialog,
  wordCount, downloadFile, type StoryMode,
} from "./JournalEntryComponents";
import { useLocale } from "@/hooks/useLocale";

export interface JournalEntry {
  id: string;
  type: "diary" | "story";
  content: string;
  turn: number;
  fromTurn?: number;
  toTurn?: number;
  createdAt: string;
  wordCount: number;
  debug?: any;
}

interface JournalPanelProps {
  adventureId?: string;
  worldId?: string;
  characterName?: string;
  turn: number;
  entries: JournalEntry[];
  onEntriesChange: (entries: JournalEntry[]) => void;
}

export function JournalPanel({ adventureId, worldId, characterName, turn, entries, onEntriesChange }: JournalPanelProps) {
  const { t } = useLocale();
  const [isGenerating, setIsGenerating] = useState<"diary" | "story" | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [readerMode, setReaderMode] = useState<"story" | "diary" | null>(null);
  const [writerDialogType, setWriterDialogType] = useState<"diary" | "story" | null>(null);
  const [writerSettings, setWriterSettings] = useState<StoryWriterSettings>(loadWriterSettings(adventureId || "", "story"));
  const [writerLabel, setWriterLabel] = useState<string | undefined>();
  const [showStoryModeDialog, setShowStoryModeDialog] = useState(false);
  const [pendingStoryMode, setPendingStoryMode] = useState<StoryMode>("continue");
  const [pendingChapterIndex, setPendingChapterIndex] = useState<number | undefined>();

  const diaryEntries = entries.filter((e) => e.type === "diary");
  const storyChapters = entries.filter((e) => e.type === "story");
  const lastStoryTurn = storyChapters.length > 0 ? Math.max(...storyChapters.map((c) => c.toTurn || 0)) : 0;
  const canGenerateStory = turn > lastStoryTurn;
  const name = characterName || "Adventurer";

  useEffect(() => {
    if (!worldId || !adventureId) return;
    loadWorldWriterDefaults(worldId).then((defaults) => {
      if (defaults) {
        const saved = loadWriterSettings(adventureId, "story");
        setWriterSettings({ ...defaults, ...saved } as StoryWriterSettings);
      }
    });
  }, [worldId, adventureId]);

  const openWriterDialog = useCallback((type: "diary" | "story", label?: string) => {
    if (!adventureId) return;
    setWriterSettings(loadWriterSettings(adventureId, type));
    setWriterLabel(label);
    setWriterDialogType(type);
  }, [adventureId]);

  const handleStoryButtonClick = useCallback(() => {
    if (storyChapters.length > 0) {
      setShowStoryModeDialog(true);
    } else {
      setPendingStoryMode("continue");
      setPendingChapterIndex(undefined);
      openWriterDialog("story", "New Story");
    }
  }, [storyChapters.length, openWriterDialog]);

  const handleStoryModeSelect = useCallback((mode: StoryMode, chapterIndex?: number) => {
    setShowStoryModeDialog(false);
    setPendingStoryMode(mode);
    setPendingChapterIndex(chapterIndex);
    const labels: Record<StoryMode, string> = {
      continue: "Continue Story",
      rewrite_chapter: `Rewrite Chapter ${(chapterIndex ?? 0) + 1}`,
      rewrite_all: "Rewrite All",
    };
    openWriterDialog("story", labels[mode]);
  }, [openWriterDialog]);

  const generate = useCallback(async (type: "diary" | "story", settings: StoryWriterSettings) => {
    if (!adventureId || isGenerating) return;
    setIsGenerating(type);
    saveWriterSettings(adventureId, type, settings);

    try {
      const body: any = {
        adventure_id: adventureId, type,
        guidelines: settings.guidelines.trim() || undefined,
        use_rag: settings.use_rag, include_journal: settings.include_journal,
        writing_length: settings.writing_length,
        style_preset: settings.style_preset || undefined,
        custom_style: settings.custom_style.trim() || undefined,
        pov: settings.pov, tone: settings.tone || undefined,
      };

      if (type === "story") {
        body.mode = pendingStoryMode;
        if (pendingStoryMode === "continue") {
          body.from_turn = lastStoryTurn;
        } else if (pendingStoryMode === "rewrite_chapter" && pendingChapterIndex != null) {
          const ch = storyChapters[pendingChapterIndex];
          if (ch) { body.from_turn = ch.fromTurn || 1; body.to_turn = ch.toTurn || turn; body.chapter_index = pendingChapterIndex; }
        }
      }

      const { data, error } = await supabase.functions.invoke("generate-journal", { body });
      if (error) throw error;
      if (!data?.content && !data?.chapters) throw new Error("No content generated");

      const creditsUsed = data.credits_used ? ` (${data.credits_used.toFixed(4)} cr)` : "";

      if (pendingStoryMode === "rewrite_all" && data.chapters) {
        const newChapters: JournalEntry[] = data.chapters.map((ch: any) => ({
          id: crypto.randomUUID(), type: "story" as const, content: ch.content, turn,
          fromTurn: ch.fromTurn || 1, toTurn: ch.toTurn || turn,
          createdAt: new Date().toISOString(), wordCount: wordCount(ch.content), debug: ch.debug || null,
        }));
        onEntriesChange([...entries.filter((e) => e.type !== "story"), ...newChapters]);
        toast.success(`Story rewritten — ${newChapters.length} chapters${creditsUsed}`);
      } else if (pendingStoryMode === "rewrite_chapter" && pendingChapterIndex != null) {
        const updated = [...entries];
        const storyIdx = entries.findIndex((e) => e === storyChapters[pendingChapterIndex]);
        if (storyIdx >= 0) {
          updated[storyIdx] = { ...updated[storyIdx], content: data.content, wordCount: wordCount(data.content), createdAt: new Date().toISOString(), debug: data.debug || null };
        }
        onEntriesChange(updated);
        toast.success(`Chapter ${pendingChapterIndex + 1} rewritten${creditsUsed}`);
      } else {
        const newEntry: JournalEntry = {
          id: crypto.randomUUID(), type, content: data.content, turn,
          fromTurn: type === "story" ? (lastStoryTurn || 1) : undefined,
          toTurn: type === "story" ? turn : undefined,
          createdAt: new Date().toISOString(), wordCount: wordCount(data.content), debug: data.debug || null,
        };
        onEntriesChange([...entries, newEntry]);
        setExpandedEntry(newEntry.id);
        toast.success((type === "diary" ? "Journal entry written!" : "Story chapter generated!") + creditsUsed);
      }
    } catch (err: any) {
      console.error("Journal generation error:", err);
      toast.error(err.message || "Failed to generate");
    } finally {
      setIsGenerating(null);
    }
  }, [adventureId, isGenerating, entries, turn, lastStoryTurn, onEntriesChange, pendingStoryMode, pendingChapterIndex, storyChapters]);

  const copyContent = useCallback((id: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success(t.copied);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const downloadEntryMd = useCallback((entry: JournalEntry, index: number) => {
    const label = entry.type === "story" ? `Chapter ${index + 1}` : `Entry ${index + 1}`;
    downloadFile(`# ${label}\n\n${entry.content}`, `${name}-${label.toLowerCase().replace(/\s+/g, "-")}.md`, "text/markdown");
  }, [name]);

  const downloadAll = useCallback((type: "story" | "diary", format: "md" | "json") => {
    const items = type === "story" ? storyChapters : diaryEntries;
    const label = type === "story" ? "Story" : "Journal";
    if (format === "md") {
      const content = items.map((e, i) => `## ${type === "story" ? `Chapter ${i + 1}` : `Entry ${i + 1}`}\n\n${e.content}`).join("\n\n---\n\n");
      downloadFile(`# ${name}'s ${label}\n\n${content}`, `${name.toLowerCase()}-${type}.md`, "text/markdown");
    } else {
      downloadFile(JSON.stringify({ exported_at: new Date().toISOString(), entries: items }, null, 2), `${name.toLowerCase()}-${type}.json`, "application/json");
    }
    toast.success(`${label} downloaded`);
  }, [storyChapters, diaryEntries, name]);

  if (!adventureId) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground/50">
        <BookOpen className="w-8 h-8" />
        <p className="text-xs">No adventure loaded</p>
      </div>
    );
  }

  const totalStoryWords = storyChapters.reduce((s, e) => s + e.wordCount, 0);

  return (
    <>
      <StoryModeDialog
        open={showStoryModeDialog}
        onSelect={handleStoryModeSelect}
        onCancel={() => setShowStoryModeDialog(false)}
        chapters={storyChapters}
      />

      {writerDialogType && (
        <StoryWriterDialog
          open={!!writerDialogType}
          type={writerDialogType}
          settings={writerSettings}
          onSettingsChange={setWriterSettings}
          onConfirm={() => { const t = writerDialogType; setWriterDialogType(null); generate(t, writerSettings); }}
          onCancel={() => setWriterDialogType(null)}
          isGenerating={!!isGenerating}
          modeLabel={writerLabel}
          hasDiaryEntries={diaryEntries.length > 0}
        />
      )}

      {readerMode && (
        <EntryCollectionReader
          entries={readerMode === "story" ? storyChapters : diaryEntries}
          title={`${name}'s ${readerMode === "story" ? "Story" : "Journal"}`}
          filenameBase={`${name.toLowerCase()}-${readerMode}`}
          onClose={() => setReaderMode(null)}
        />
      )}

      <div className="flex flex-col h-full">
        {/* ── Generate buttons ── */}
        <div className="flex gap-2 pb-3">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 text-xs h-9"
            onClick={() => openWriterDialog("diary", "Journal Entry")}
            disabled={!!isGenerating}
          >
            {isGenerating === "diary" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
            Diary
          </Button>
          <Button
            variant={canGenerateStory || storyChapters.length > 0 ? "default" : "outline"}
            size="sm"
            className="flex-1 gap-1.5 text-xs h-9"
            onClick={handleStoryButtonClick}
            disabled={!!isGenerating || (!canGenerateStory && storyChapters.length === 0)}
          >
            {isGenerating === "story" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookText className="w-3.5 h-3.5" />}
            Story
            {canGenerateStory && lastStoryTurn > 0 && (
              <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-auto">+{turn - lastStoryTurn}</Badge>
            )}
          </Button>
        </div>

        {!canGenerateStory && storyChapters.length > 0 && (
          <p className="text-[10px] text-muted-foreground text-center pb-2">Story caught up — play more or rewrite.</p>
        )}

        {/* ── Entries ── */}
        <ScrollArea className="flex-1 min-h-0">
          {entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground/40">
              <BookOpen className="w-8 h-8" />
              <p className="text-xs">No entries yet</p>
              <p className="text-[10px] text-muted-foreground/30">Write a diary entry or generate a story</p>
            </div>
          ) : (
            <div className="space-y-4 pr-2">
              {/* Story section */}
              {storyChapters.length > 0 && (
                <section>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                      <BookText className="w-3 h-3" />
                      Story · {totalStoryWords}w
                    </h3>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setReaderMode("story")} title="Read full story" aria-label="Read full story">
                        <Eye className="w-3 h-3" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Download" aria-label="Download story">
                            <Download className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[120px]">
                          <DropdownMenuItem className="text-xs" onClick={() => downloadAll("story", "md")}>
                            Download .md
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-xs" onClick={() => downloadAll("story", "json")}>
                            Download .json
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {storyChapters.map((entry, index) => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        index={index + 1}
                        isExpanded={expandedEntry === entry.id}
                        onToggle={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        onCopy={() => copyContent(entry.id, entry.content)}
                        isCopied={copiedId === entry.id}
                        onDownloadMd={() => downloadEntryMd(entry, index)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Diary section */}
              {diaryEntries.length > 0 && (
                <section>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                      <PenLine className="w-3 h-3" />
                      Diary · {diaryEntries.length}
                    </h3>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setReaderMode("diary")} title="Read full journal" aria-label="Read full journal">
                        <Eye className="w-3 h-3" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Download" aria-label="Download journal">
                            <Download className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[120px]">
                          <DropdownMenuItem className="text-xs" onClick={() => downloadAll("diary", "md")}>
                            Download .md
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-xs" onClick={() => downloadAll("diary", "json")}>
                            Download .json
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {diaryEntries.map((entry, index) => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        index={index + 1}
                        isExpanded={expandedEntry === entry.id}
                        onToggle={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        onCopy={() => copyContent(entry.id, entry.content)}
                        isCopied={copiedId === entry.id}
                        onDownloadMd={() => downloadEntryMd(entry, index)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </ScrollArea>
      </div>
    </>
  );
}